'use client'

import { ChevronDownIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { type ReactNode, useId, useState } from 'react'

import { SectionHeading } from './SectionHeading'

const FAQ_ITEMS: { question: string; answer: ReactNode }[] = [
  {
    question: 'Who can attend The Retreat?',
    answer: (
      <p>
        The Retreat is open to magicians of all levels, from enthusiastic
        hobbyists to seasoned professionals. At the same time, the experience is
        thoughtfully designed for spouses and partners as well, so many
        attendees bring a guest who enjoys travel, culture, and shared
        experiences. The programme allows both magicians and their partners to
        fully enjoy the journey together—from exploring the Scottish Highlands
        during the day to sharing the evening performances and gatherings.
      </p>
    ),
  },
  {
    question: 'When should I arrive?',
    answer: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-stone-900">The Retreat schedule</p>
          <p>2 September — Arrive in Edinburgh</p>
          <p>7 September — Depart from Edinburgh</p>
        </div>
        <div>
          <p className="font-semibold text-stone-900">
            Extend the Magic schedule
          </p>
          <p>7 September — Transfer to Cameron House, Loch Lomond</p>
          <p>8 September — Cameron House</p>
          <p>9 September — Depart from Loch Lomond and transfer to Edinburgh</p>
        </div>
        <p>
          Please plan to arrive in Edinburgh no later than the afternoon of 2
          September. Departure flights may be booked after 2pm on 7 September
          for Retreat-only guests, or after 3pm on 9 September for guests
          joining the Cameron House add-on.
        </p>
      </div>
    ),
  },
  {
    question: 'Do you cater for solo travellers?',
    answer: (
      <p>
        Yes. Whilst we absolutely encourage you to bring a partner or guest to
        experience The Retreat, we warmly welcome solo travellers. Many of our
        past guests have joined solo and left with lifelong memories and
        friends. The Retreat is very much a family.
      </p>
    ),
  },
  {
    question: 'Which airport should I fly into?',
    answer: (
      <p>
        The Retreat begins and ends in Edinburgh, Scotland. Edinburgh Airport is
        the nearest international airport.
      </p>
    ),
  },
  {
    question: 'How active is The Retreat?',
    answer: (
      <p>
        The itinerary includes guided explorations of various sites. Guests are
        always free to enjoy the surroundings in their own way. The journey has
        been thoughtfully designed so that everyone can fully embrace the
        experience, whether that means exploring every corner of the places we
        visit or simply soaking in the extraordinary landscapes and atmosphere.
      </p>
    ),
  },
  {
    question: 'Will there be magic performances and sessions?',
    answer: (
      <p>
        Yes. The Retreat includes a number of one-of-a-kind magic sessions and
        performances throughout the experience. The whole time, you&apos;ll be
        travelling and dining with the performers and the Vanishing Inc. team,
        including co-founders Andi Gladwin and Joshua Jay.
      </p>
    ),
  },
  {
    question: 'What happens after I book?',
    answer: (
      <p>
        Once your place is confirmed, you will receive detailed travel guidance,
        packing suggestions, and itinerary information to help you prepare for
        the journey. Our team will also be available to assist with any
        questions leading up to The Retreat, along with access to our special
        attendee Facebook group.
      </p>
    ),
  },
  {
    question: 'Is travel insurance required?',
    answer: (
      <p>
        Yes. Because this is an international travel experience, all guests are
        required to obtain comprehensive travel insurance covering trip
        cancellation, medical expenses, and travel disruptions.
      </p>
    ),
  },
  {
    question: 'What is the booking policy?',
    answer: (
      <p>
        You can choose to pay in full or in easy instalments during the
        registration process. As The Retreats consistently sell out—and to
        ensure the highest quality experience—we commit to bookings,
        reservations, and service providers well in advance. As a result,
        registrations are non-refundable, and travel insurance is a
        prerequisite.
      </p>
    ),
  },
]

export function FAQ() {
  const baseId = useId()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section
      className="mx-auto max-w-(--breakpoint-xl) px-4 py-12 sm:px-6 md:py-20"
      aria-labelledby={`${baseId}-heading`}
    >
      <SectionHeading id={`${baseId}-heading`} centered>
        Frequently Asked Questions
      </SectionHeading>

      <div className="mx-auto mt-7 max-w-4xl border-t border-stone-300">
        {FAQ_ITEMS.map((item, index) => {
          const triggerId = `${baseId}-trigger-${index}`
          const panelId = `${baseId}-panel-${index}`
          const isOpen = openIndex === index

          return (
            <div key={item.question} className="border-b border-stone-300">
              <h3 className="m-0 font-scotland text-xl font-bold sm:text-2xl">
                <button
                  id={triggerId}
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-between gap-4 py-5 text-left text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#744e68] focus-visible:ring-offset-2"
                  onClick={() =>
                    setOpenIndex(previous =>
                      previous === index ? null : index
                    )
                  }
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span>{item.question}</span>
                  <ChevronDownIcon
                    className={clsx(
                      'size-6 shrink-0 text-[#744e68] transition-transform duration-200',
                      isOpen && 'rotate-180'
                    )}
                    aria-hidden
                  />
                </button>
              </h3>

              {isOpen && (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={triggerId}
                  className="border-t border-stone-200 bg-stone-50 px-5 py-5 text-lg leading-relaxed text-stone-700"
                >
                  {item.answer}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
